/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type Allocation from '@linc-technologies/linced-common/models/bookings/allocation';
import type Event from '@linc-technologies/linced-common/models/bookings/event';
import type Person from '@linc-technologies/linced-common/models/person';
import type School from '@linc-technologies/linced-common/models/school';

// Proto: bookings.Booking.
export default class Booking extends Model {
  declare [Type]: 'bookings/booking';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // eventId is the id of the event.
  @belongsTo('bookings/event', { async: true, inverse: 'bookings' })
  declare eventId: AsyncBelongsTo<Event>;

  @alias('eventId')
  declare event: AsyncBelongsTo<Event>;

  @alias('event.id')
  declare event_id: string | null | undefined;

  // allocationId is the id of the allocation the booking relates to.
  @belongsTo('bookings/allocation', { async: true, inverse: 'bookings' })
  declare allocationId: AsyncBelongsTo<Allocation>;

  @alias('allocationId')
  declare allocation: AsyncBelongsTo<Allocation>;

  @alias('allocation.id')
  declare allocation_id: string | null | undefined;

  // caregiverId is the id of the caregiver the booking relates to.
  @belongsTo('person', { async: true, inverse: null })
  declare caregiverId: AsyncBelongsTo<Person>;

  @alias('caregiverId')
  declare caregiver: AsyncBelongsTo<Person>;

  @alias('caregiver.id')
  declare caregiver_id: string | null | undefined;

  // studentId is the id of the student the booking relates to.
  @belongsTo('person', { async: true, inverse: null })
  declare studentId: AsyncBelongsTo<Person>;

  @alias('studentId')
  declare student: AsyncBelongsTo<Person>;

  @alias('student.id')
  declare student_id: string | null | undefined;

  // authorId is the id of the person who created the booking.
  @belongsTo('person', { async: true, inverse: null })
  declare authorId: AsyncBelongsTo<Person>;

  @alias('authorId')
  declare author: AsyncBelongsTo<Person>;

  @alias('author.id')
  declare author_id: string | null | undefined;

  // onlineMeetingURL is the meeting link for online meetings.
  @attr('string', { defaultValue: '' })
  declare onlineMeetingURL: string;

  // caregiverNote is the note added by caregiver.
  @attr('string', { defaultValue: '' })
  declare caregiverNote: string;

  // staffNote is the note added by staff. It is not visible to caregivers.
  @attr('string', { defaultValue: '' })
  declare staffNote: string;

  // cancelledBy is the id of person who cancelled the booking.
  @belongsTo('person', { async: true, inverse: null })
  declare cancelledBy: AsyncBelongsTo<Person>;

  @alias('cancelledBy.id')
  declare cancelledBy_id: string | null | undefined;

  // cancelledAt is the time when the booking was cancelled in seconds from the epoch.
  @attr('number')
  declare cancelledAt: number | undefined;

  // schoolId is the school the booking relates to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // campusId is the school campus the booking relates to.
  @attr('string', { defaultValue: '' })
  declare campusId: string;
}
